import secureApiService from './secureApiService'
import { CertificateModel } from '@/models/certificate.model'
import { PaginatedResponse, normalizePaginationMeta } from '@/models/templates/pagination.model'
import { sanitizeInput } from '@/utils/security'

interface CertificateJSON {
  [key: string]: any
}

interface CertificateListResponseJSON {
  success: boolean
  message?: string
  data?: {
    data?: CertificateJSON[]
    meta?: Record<string, unknown> | null
  } | CertificateJSON[] | CertificateJSON | null
  meta?: Record<string, unknown> | null
}

interface CertificateSingleResponseJSON {
  success: boolean
  message?: string
  data?: CertificateJSON | null
}

interface VerifyCertificateResponseJSON {
  success: boolean
  message?: string
  data?: {
    ok?: boolean
    data?: CertificateJSON | null
  } | null
}

interface RevokeCertificateResponseJSON {
  success: boolean
  message?: string
  data?: {
    certificateId?: string | null
  } | null
}

export interface CertificateListParams {
  page?: number
  perPage?: number
  userId?: string
  type?: string
  programmeId?: string
  courseId?: string
}

export interface UploadCertificatePayload {
  file: File
  userId: string
  title: string
  certificateNumber: string
  replace?: boolean
  notify?: boolean
}

export interface RevokeCertificatePayload {
  reason: string
}

class CertificatesService {
  private basePath = '/certificates'

  async getCertificates(params: CertificateListParams = {}): Promise<PaginatedResponse<CertificateModel>> {
    const searchParams = new URLSearchParams()
    if (params.page) searchParams.set('page', String(params.page))
    if (params.perPage) searchParams.set('perPage', String(params.perPage))
    if (params.userId) searchParams.set('userId', params.userId)
    if (params.type) searchParams.set('type', params.type)
    if (params.programmeId) searchParams.set('programmeId', params.programmeId)
    if (params.courseId) searchParams.set('courseId', params.courseId)

    const endpoint = searchParams.toString() ? `${this.basePath}?${searchParams.toString()}` : this.basePath
    const response = await secureApiService.get<CertificateListResponseJSON>(endpoint, { requireAuth: true })
    const payload = response.data

    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to fetch certificates')
    }

    const rawData: CertificateJSON[] =
      Array.isArray(payload.data) ?
        (payload.data as CertificateJSON[]) :
        Array.isArray((payload.data as any)?.data) ?
          ((payload.data as any).data as CertificateJSON[]) :
          payload.data ?
            [payload.data as CertificateJSON] :
            []

    const data = rawData.map((item: CertificateJSON) => CertificateModel.fromJSON(item))
    const meta =
      normalizePaginationMeta(
        (payload.data && typeof payload.data === 'object' && 'meta' in payload.data ? (payload.data as any).meta : undefined)
        ?? payload.meta,
      ) ?? {
        total: data.length,
        page: params.page ?? 1,
        perPage: params.perPage ?? (data.length || 25),
      }

    return { data, meta }
  }

  async getCertificateById(certificateId: string): Promise<CertificateModel> {
    if (!certificateId) {
      throw new Error('Certificate ID is required')
    }

    const endpoint = `${this.basePath}/${encodeURIComponent(certificateId)}`
    const response = await secureApiService.get<CertificateSingleResponseJSON>(endpoint, { requireAuth: true })

    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.message || 'Unable to fetch certificate')
    }

    return CertificateModel.fromJSON(response.data.data)
  }

  async uploadCertificate(payload: UploadCertificatePayload): Promise<CertificateModel> {
    const formData = new FormData()
    formData.append('file', payload.file)
    formData.append('userId', sanitizeInput(payload.userId))
    formData.append('title', sanitizeInput(payload.title))
    formData.append('certificateNumber', sanitizeInput(payload.certificateNumber))

    if (typeof payload.replace === 'boolean') {
      formData.append('replace', String(payload.replace))
    }

    if (typeof payload.notify === 'boolean') {
      formData.append('notify', String(payload.notify))
    }

    const response = await secureApiService.postFormData<CertificateSingleResponseJSON>(
      `${this.basePath}/upload`,
      formData,
      { requireAuth: true },
    )

    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.message || 'Unable to upload certificate')
    }

    return CertificateModel.fromJSON(response.data.data)
  }

  async revokeCertificate(certificateId: string, payload: RevokeCertificatePayload): Promise<string | null> {
    if (!certificateId) {
      throw new Error('Certificate ID is required')
    }

    const endpoint = `${this.basePath}/${encodeURIComponent(certificateId)}/revoke`
    const response = await secureApiService.post<RevokeCertificateResponseJSON>(endpoint, payload, { requireAuth: true })

    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Unable to revoke certificate')
    }

    return response.data.data?.certificateId ?? certificateId
  }

  async verifyCertificate(certificateNumber: string, token?: string): Promise<CertificateModel> {
    if (!certificateNumber) {
      throw new Error('Certificate number is required')
    }

    const search = token ? `?token=${encodeURIComponent(token)}` : ''
    const endpoint = `${this.basePath}/verify/${encodeURIComponent(certificateNumber)}${search}`
    const response = await secureApiService.get<VerifyCertificateResponseJSON>(endpoint, { requireAuth: false })

    const payload = response.data
    const rawData = payload?.data && 'data' in payload.data ? payload.data.data : payload?.data

    if (!payload?.success || !rawData) {
      throw new Error(payload?.message || 'Unable to verify certificate')
    }

    return CertificateModel.fromJSON(rawData)
  }
}

export const certificatesService = new CertificatesService()
export default certificatesService
